import type { LucideIcon } from 'lucide-react';
import {
  MessageCircle,
  Clock,
  FileText,
  CreditCard,
  BarChart,
  Users,
} from 'lucide-react';

export type LandingHighlight = {
  id: string;
  title: string;
  summary: string;
  chips: [string, string];
  icon: LucideIcon;
  accent: 'green' | 'cyan' | 'purple' | 'amber' | 'blue' | 'orange';
};

export const HIGHLIGHT_ACCENTS: Record<
  LandingHighlight['accent'],
  {
    card: string;
    icon: string;
    chip: string;
    glow: string;
  }
> = {
  green: {
    card: 'from-green-50/90 to-white',
    icon: 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-green-500/25',
    chip: 'bg-green-100/90 text-green-800 border-green-200/60',
    glow: 'bg-green-400/15',
  },
  cyan: {
    card: 'from-cyan-50/90 to-white',
    icon: 'bg-gradient-to-br from-cyan-500 to-sky-600 text-white shadow-cyan-500/25',
    chip: 'bg-cyan-100/90 text-cyan-800 border-cyan-200/60',
    glow: 'bg-cyan-400/15',
  },
  purple: {
    card: 'from-purple-50/90 to-white',
    icon: 'bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-purple-500/25',
    chip: 'bg-purple-100/90 text-purple-800 border-purple-200/60',
    glow: 'bg-purple-400/15',
  },
  amber: {
    card: 'from-amber-50/90 to-white',
    icon: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-amber-500/25',
    chip: 'bg-amber-100/90 text-amber-900 border-amber-200/60',
    glow: 'bg-amber-400/15',
  },
  orange: {
    card: 'from-orange-50/90 to-white',
    icon: 'bg-gradient-to-br from-orange-500 to-amber-600 text-white shadow-orange-500/25',
    chip: 'bg-orange-100/90 text-orange-900 border-orange-200/60',
    glow: 'bg-orange-400/15',
  },
  blue: {
    card: 'from-blue-50/90 to-white',
    icon: 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-blue-500/25',
    chip: 'bg-blue-100/90 text-blue-800 border-blue-200/60',
    glow: 'bg-blue-400/15',
  },
};

export const LANDING_PLAN_HIGHLIGHTS: LandingHighlight[] = [
  {
    id: 'whatsapp',
    title: 'WhatsApp messaging',
    summary:
      'Reach patients from the queue or profile with ready-made messages — opens on your phone, no API setup.',
    chips: ['Templates included', 'Prescription PDF'],
    icon: MessageCircle,
    accent: 'green',
  },
  {
    id: 'queue',
    title: 'Live patient queue',
    summary:
      'Reception and doctors share one live queue — tokens, status, and “your turn” updates stay in sync.',
    chips: ['Real-time sync', 'Per-doctor view'],
    icon: Clock,
    accent: 'cyan',
  },
  {
    id: 'prescription',
    title: 'E-prescriptions',
    summary:
      'Meds, diagnosis, and lab tests in one Rx — print on letterhead or send a PDF on WhatsApp.',
    chips: ['Print & PDF', 'Rx templates'],
    icon: FileText,
    accent: 'purple',
  },
  {
    id: 'billing',
    title: 'Billing & receipts',
    summary:
      'Bill visits with your service rates — cash, UPI, or card — and hand patients a printed receipt.',
    chips: ['Your pricing', 'Payment tracking'],
    icon: CreditCard,
    accent: 'amber',
  },
  {
    id: 'analytics',
    title: 'Reports & revenue',
    summary:
      'Daily visits and collections on one dashboard — see trends without spreadsheets.',
    chips: ['Visit insights', 'Private to you'],
    icon: BarChart,
    accent: 'orange',
  },
  {
    id: 'patients',
    title: 'Unlimited scale',
    summary:
      'No cap on patients, doctors, or staff — every visit and prescription stays in one history.',
    chips: ['Unlimited users', 'Full history'],
    icon: Users,
    accent: 'blue',
  },
];
